import { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import PageHeader from "../components/PageHeader";
import { MapPin, Mail, Clock, Send } from "lucide-react";
import { CONTACT } from "../constants/testIds";
import { useLang } from "../i18n/LanguageProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.errorRequired"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(t("contact.successToast"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("contact.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster theme="dark" position="top-center" />
      <PageHeader
        eyebrow={t("page.contact.eyebrow")}
        title={t("page.contact.title")}
        lede={t("page.contact.lede")}
      />

      <section className="pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <aside className="lg:col-span-5 space-y-10">
            <div>
              <div className="eyebrow mb-4">{t("footer.office")}</div>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-gold mt-1" strokeWidth={1.4} />
                <address className="not-italic text-white/90 leading-relaxed">
                  Bonworld Family Office<br />
                  111 North Bridge Road<br />
                  #15-02, Peninsula Plaza<br />
                  Singapore 179098
                </address>
              </div>
            </div>
            <div>
              <div className="eyebrow mb-4">Correspondence</div>
              <div className="flex items-start gap-4">
                <Mail size={18} className="text-gold mt-1" strokeWidth={1.4} />
                <div>
                  <a href="mailto:office@bonworldsingapore.com" className="text-white hover:text-gold" data-testid="contact-email-link">office@bonworldsingapore.com</a>
                  <div className="text-muted text-sm mt-1">Encrypted correspondence available on request.</div>
                </div>
              </div>
            </div>
            <div>
              <div className="eyebrow mb-4">Hours</div>
              <div className="flex items-start gap-4">
                <Clock size={18} className="text-gold mt-1" strokeWidth={1.4} />
                <div className="text-white/90">
                  Monday — Friday, 09:00 — 18:00 SGT<br />
                  <span className="text-muted text-sm">Weekends and after-hours meetings by prior arrangement.</span>
                </div>
              </div>
            </div>

            <div className="border border-[var(--bw-border)] p-6 mt-8">
              <div className="text-gold text-xs tracking-[0.32em] uppercase mb-3">A note on discretion</div>
              <p className="text-muted text-sm leading-relaxed">
                Please share only what is necessary in your initial message. Sensitive details
                are best exchanged in person or via an encrypted channel established at first
                contact.
              </p>
            </div>
          </aside>

          <div className="lg:col-span-7">
            {sent ? (
              <div data-testid={CONTACT.success} className="border border-[var(--bw-border)] p-10 lg:p-14">
                <div className="eyebrow mb-6">{t("contact.receivedEyebrow")}</div>
                <h3 className="font-serif-display text-3xl text-white mb-4">{t("contact.thankyou")}</h3>
                <p className="text-muted leading-relaxed">{t("contact.thankyouLede")}</p>
                <button
                  onClick={() => setSent(false)}
                  className="btn-ghost mt-8"
                  data-testid="contact-send-another"
                >
                  {t("contact.sendAnother")}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid={CONTACT.form} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <label className="block">
                    <span className="text-[10px] tracking-[0.32em] uppercase text-muted">{t("contact.name")}</span>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={onChange("name")}
                      className="input-lux"
                      data-testid={CONTACT.name}
                      placeholder={t("contact.namePlace")}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] tracking-[0.32em] uppercase text-muted">{t("contact.email")}</span>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange("email")}
                      className="input-lux"
                      data-testid={CONTACT.email}
                      placeholder="you@example.com"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <label className="block">
                    <span className="text-[10px] tracking-[0.32em] uppercase text-muted">{t("contact.phone")}</span>
                    <input
                      type="text"
                      value={form.phone}
                      onChange={onChange("phone")}
                      className="input-lux"
                      data-testid={CONTACT.phone}
                      placeholder="+65 ..."
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] tracking-[0.32em] uppercase text-muted">{t("contact.subject")}</span>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={onChange("subject")}
                      className="input-lux"
                      data-testid={CONTACT.subject}
                      placeholder={t("contact.subjectPlace")}
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[10px] tracking-[0.32em] uppercase text-muted">{t("contact.message")}</span>
                  <textarea
                    required
                    value={form.message}
                    onChange={onChange("message")}
                    className="input-lux textarea-lux"
                    data-testid={CONTACT.message}
                    placeholder={t("contact.messagePlace")}
                  />
                </label>

                <div className="pt-4 flex items-center justify-between gap-4">
                  <p className="text-muted text-xs max-w-sm leading-relaxed">
                    {t("contact.consent")}
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-gold"
                    data-testid={CONTACT.submit}
                  >
                    {loading ? t("contact.sending") : (<>{t("contact.send")} <Send size={14} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
